import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Moon, Sun, Monitor, Coffee } from "lucide-react";

const THEME_KEY = "ccs_theme";
export const THEMES = [
    { id: "dark", label: "Dark", icon: Moon },
    { id: "light", label: "Light", icon: Sun },
    { id: "coffee", label: "Coffee", icon: Coffee },
    { id: "latte", label: "Light Coffee", icon: Coffee },
    { id: "system", label: "System", icon: Monitor },
];

const VALID_THEME_IDS = new Set(THEMES.map((t) => t.id));

const systemPref = () =>
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
    // Dark is the house default; "system" follows the OS preference live.
    // localStorage is acceptable here: theme is a non-sensitive UI preference
    // (no auth/user data). Session tokens live in httpOnly cookies only.
    const [theme, setThemeState] = useState(() => {
        const stored = localStorage.getItem(THEME_KEY);
        return stored && VALID_THEME_IDS.has(stored) ? stored : "light";
    });

    const setTheme = (id) => setThemeState(id);

    useEffect(() => {
        const apply = () => {
            const resolved = theme === "system" ? systemPref() : theme;
            document.documentElement.setAttribute("data-theme", resolved);
            document.body.setAttribute("data-theme", resolved);
            document.documentElement.style.colorScheme = resolved === "light" || resolved === "latte" ? "light" : "dark";
        };
        apply();
        localStorage.setItem(THEME_KEY, theme);
        if (theme === "system") {
            const mq = window.matchMedia("(prefers-color-scheme: dark)");
            mq.addEventListener("change", apply);
            return () => mq.removeEventListener("change", apply);
        }
    }, [theme]);

    const value = useMemo(
        () => ({ theme, setTheme, themes: THEMES }),
        [theme],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
    return ctx;
}
